import { useCallback, useMemo, useState } from 'react';
import {
  FireflyReframeDialog,
  FireflyRemoveBackgroundDialog,
  FireflyTranslateConsentDialog,
  FireflyTranslateDialog,
} from '@frameio/firefly-actions';
import { useSelectionForOperations } from '../../selection';

export const useManageGenerateAssetsDialog = () => {
  const [isShowing, setIsShowing] = useState(false);
  const { selectedIds: selectedAssetIds, assetTypeMap } =
    useSelectionForOperations(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [translateStep, setTranslateStep] = useState<
    'consent' | 'dialog' | null
  >(null);
  const [consentTimestamp, setConsentTimestamp] = useState<string | null>(null);

  const selectedVideoAssets = useMemo(
    () => selectedAssetIds.filter((id) => assetTypeMap[id] === 'VideoAsset'),
    [assetTypeMap, selectedAssetIds]
  );

  const selectedImageAssets = useMemo(
    () => selectedAssetIds.filter((id) => assetTypeMap[id] === 'ImageAsset'),
    [assetTypeMap, selectedAssetIds]
  );

  const onCancel = useCallback(({ callback }: { callback?: () => void }) => {
    callback?.();
    setIsShowing(false);
    setSelectedAction(null);
    setTranslateStep(null);
  }, []);

  const createGenerateAssetsDialog = useCallback(
    ({ onClose }: { onClose?: () => void }) => {
      if (!isShowing) return null;

      if (selectedAction === 'reframeVideo') {
        const filteredAssetIds = selectedVideoAssets;
        return (
          <FireflyReframeDialog
            assetIds={filteredAssetIds}
            onClose={() => onCancel({ callback: onClose })}
          />
        );
      }
      if (selectedAction === 'removeBackground') {
        if (selectedImageAssets.length === 0) return null;
        return (
          <FireflyRemoveBackgroundDialog
            assetIds={selectedImageAssets}
            onClose={() => onCancel({ callback: onClose })}
          />
        );
      }
      if (selectedAction === 'translateVideo') {
        if (selectedVideoAssets.length === 0) return null;
        return (
          <>
            {translateStep === 'consent' && (
              <FireflyTranslateConsentDialog
                assetCount={selectedVideoAssets.length}
                isShowing={isShowing}
                onAgree={(ts: string) => {
                  setTranslateStep('dialog');
                  setConsentTimestamp(ts);
                }}
                onCancel={() => onCancel({ callback: onClose })}
              />
            )}
            {translateStep === 'dialog' && (
              <FireflyTranslateDialog
                assetIds={selectedVideoAssets}
                consentTimestamp={consentTimestamp || undefined}
                isShowing={isShowing}
                onClose={() => onCancel({ callback: onClose })}
              />
            )}
          </>
        );
      }
      return null;
    },
    [
      isShowing,
      selectedVideoAssets,
      selectedImageAssets,
      selectedAction,
      translateStep,
      consentTimestamp,
      onCancel,
    ]
  );

  const openGenerateAssetsDialog = (action?: string) => {
    setIsShowing(true);
    setSelectedAction(action ?? null);
    setTranslateStep(action === 'translateVideo' ? 'consent' : null);
  };

  return {
    openGenerateAssetsDialog,
    createGenerateAssetsDialog,
    isShowing,
  };
};
